import { CONNECTOR_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input, Skeleton } from "@sagot/ui";
import { useId, useState } from "react";
import type { HttpRequestStep } from "../types";
import { placeholdersOf, requestProblem, sampleFor, testRequestOf, withMappingFor } from "./connector-model";
import { ConnectorTestResult } from "./connector-test-result";
import { testFailureOf, useConnectorTest } from "./use-connector-test";

const C = "engage.workflows.connector.test";

interface PanelProps {
  step: HttpRequestStep;
  /** The plan does not include the connector: nothing can be tested. */
  locked: boolean;
  planName: string;
  disabled: boolean;
  onChange: (step: HttpRequestStep) => void;
}

function SampleFields({ names, values, disabled, onChange }: { names: string[]; values: Record<string, string>; disabled: boolean; onChange: (name: string, value: string) => void }) {
  const { t } = useTranslation();
  if (names.length === 0) return null;
  return (
    <fieldset className="space-y-2" data-testid="connector-sample">
      <legend className="label">{t(`${C}.sample`)}</legend>
      <p className="hint -mt-1">{t(`${C}.sampleHint`)}</p>
      {names.map((name) => (
        <label key={name} className="flex flex-wrap items-center gap-2 text-xs">
          <code className="w-full font-mono font-semibold sm:w-48 sm:shrink-0">{`{{${name}}}`}</code>
          <Input className="min-w-0 flex-1 font-mono text-xs" placeholder={t(`${C}.samplePlaceholder`)} disabled={disabled} maxLength={CONNECTOR_LIMITS.sampleValue} value={values[name] ?? ""} onChange={(event) => { onChange(name, event.target.value); }} />
        </label>
      ))}
    </fieldset>
  );
}

/** Test request: sends the step once with sample values and shows what came back, so the mapping can be built from real fields. */
export function ConnectorTestPanel({ step, locked, planName, disabled, onChange }: PanelProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const test = useConnectorTest();
  const [typed, setTyped] = useState<Record<string, string>>({});
  const names = placeholdersOf(step);
  const problem = requestProblem(step);
  const blocked = locked || problem !== null;
  const reason = locked ? t(`${C}.locked`, { plan: planName }) : problem === "endpoint" ? t(`${C}.needEndpoint`) : problem !== null ? t(`${C}.needFix`) : null;
  const run = () => {
    if (!test.isPending) test.mutate(testRequestOf(step, sampleFor(step, typed)));
  };
  return (
    <section aria-labelledby={headingId} className="space-y-3 rounded-md bg-surface-2 p-3" data-testid="connector-test">
      <div>
        <h3 id={headingId} className="text-sm font-bold">{t(`${C}.title`)}</h3>
        <p className="mt-0.5 text-xs text-fg-muted">{t(`${C}.intro`)}</p>
      </div>
      <SampleFields names={names} values={sampleFor(step, typed)} disabled={disabled || test.isPending} onChange={(name, value) => { setTyped({ ...typed, [name]: value }); }} />
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" loading={test.isPending} disabled={disabled || blocked} aria-describedby={reason ? `${headingId}-reason` : undefined} onClick={run}>
          <Icon name="play" />
          {test.isPending ? t(`${C}.running`) : t(`${C}.run`)}
        </Button>
        {reason ? <span id={`${headingId}-reason`} className="text-xs text-fg-muted">{reason}</span> : null}
      </div>
      <div aria-live="polite" aria-busy={test.isPending}>
        {test.isPending ? (
          <div role="status" aria-label={t(`${C}.running`)} className="space-y-2" data-testid="connector-test-loading">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-64 max-w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : null}
        {test.isError ? (
          <p role="alert" className="flex items-start gap-2 text-sm font-semibold text-danger" data-testid="connector-test-error">
            <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0" />
            {t(`${C}.callFailed.${testFailureOf(test.error)}`)}
          </p>
        ) : null}
        {test.isSuccess ? <ConnectorTestResult result={test.data} step={step} disabled={disabled} onUse={(pointer) => { onChange(withMappingFor(step, pointer)); }} /> : null}
      </div>
    </section>
  );
}
